import { Box, Button, Grid, TextField, Typography, FormHelperText } from "@mui/material";
import Header from "components/Header";
import { BasePathProvider, useBasePath, useSession } from "components/Providers";
import { useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from 'yup'
import { Controller, useForm } from "react-hook-form";
import { enqueueSnackbar } from "notistack";
import { save } from "store/evaluation/admin-goal";
import { useDispatch } from "react-redux";
import _ from "lodash";
import { FileUploadField } from "components/FileUploadField";

export default function GoalForm({ goal, action }) {
    const { id } = useParams()
    const basePath = useBasePath()
    const { district } = useSession()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const schema = yup.object().shape({})

    const { control, handleSubmit, formState, setError, clearErrors } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            ...goal,
            fileId: goal?.file || null,
        }
    })

    const onSubmit = async (data) => {
        try {
            let fileId = goal?.file?.id || null;

            if (data.fileId && (!goal?.file || data.fileId.name !== goal.file.name)) {
                const formData = new FormData();
                formData.append('afile', data.fileId);
                formData.append('districtId', district.id);

                const uploadRes = await fetch('/api/file', {
                    method: 'POST',
                    body: formData,
                });

                if (!uploadRes.ok) {
                    enqueueSnackbar(uploadRes.statusText || 'File upload failed', { variant: 'error' });
                    return;
                }

                const uploadData = await uploadRes.json();
                fileId = uploadData?.id;

                if (!fileId) {
                    enqueueSnackbar('Upload succeeded but fileId missing in response.', { variant: 'error' });
                    return;
                }
            }

            delete data.fileId;
            data.fileId = fileId;

            if (Array.isArray(data.indicatorIds)) {
                data.indicatorIds = data.indicatorIds.map(ind => ind.code);
            }

            let exceptions = ["file", "adminEvaluationStandard"];
            if (id !== 'new') {
                exceptions.push("standardId");
            }
            data.standardId = action === 'iReady' ? 7 : 8;

            const res = await dispatch(save(_.omit(data, exceptions)));
            if (res.payload) {
                navigate(basePath);
            }
        } catch (error) {
            enqueueSnackbar(error.message || 'Unexpected error during submission', { variant: 'error' });
            console.error('Submission error:', error);
        }
    };

    return (
        <BasePathProvider value={`${basePath}/evaluation`}>

            <Header title={id === 'new' ? `Add ${action} Goal` : `Edit ${goal?.adminEvaluationStandard?.name}`} small close borderBottom />
            <Box data-component="EvaluationForm" component="form" onSubmit={handleSubmit(onSubmit)} p={2}>

                <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                        <Typography variant="body1" color="text.primary" fontWeight={600}>
                            {action ? `${action} Goal` : goal?.adminEvaluationStandard?.name}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name="goal"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    variant="outlined"
                                    label="Goal"
                                    multiline
                                    rows={4}
                                    fullWidth
                                    error={Boolean(formState.errors.indicatorList)}
                                    helperText={formState.errors.indicatorList?.message}
                                />


                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name="fileId"
                            control={control}
                            rules={{ required: 'File is required' }}
                            render={({ field, fieldState }) => (
                                <>
                                    <FileUploadField
                                        value={field.value || null}
                                        onChange={(file) => field.onChange(file)}
                                        onError={(msg) => {
                                            if (msg) setError(field.name, { type: "manual", message: msg });
                                            else clearErrors(field.name);
                                        }}
                                    />

                                    {fieldState.error && (
                                        <FormHelperText error sx={{ ml: 1, mt: 0.5 }}>
                                            {fieldState.error.message}
                                        </FormHelperText>
                                    )}
                                </>
                            )}
                        />
                    </Grid>
                </Grid>
                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button type="submit" size="medium" variant="contained" color="primary">
                        Save
                    </Button>
                </Box>
            </Box>
        </BasePathProvider>
    )
}
